/**
 * GitLab notification service.
 * Fetches notifications from GitLab API and integrates with the notification store.
 */
/**
 * Fetch notifications from GitLab API
 */
export async function fetchGitLabNotifications(config) {
    const { baseUrl, accessToken, projectId } = config;
    try {
        let url = `${baseUrl}/api/v4/notifications?per_page=20`;
        if (projectId) {
            url += `&project_id=${projectId}`;
        }
        const response = await fetch(url, {
            headers: {
                'PRIVATE-TOKEN': accessToken,
            },
        });
        if (!response.ok) {
            console.error('Failed to fetch GitLab notifications:', response.statusText);
            return [];
        }
        const notifications = await response.json();
        // Fetch detailed info for each notification
        const detailedNotifications = [];
        for (const notif of notifications.slice(0, 10)) {
            const detailed = notif;
            try {
                if (notif.subject_type === 'MergeRequest') {
                    const mrUrl = `${baseUrl}/api/v4/projects/${notif.project_id}/merge_requests/${notif.subject_id}`;
                    const mrRes = await fetch(mrUrl, {
                        headers: { 'PRIVATE-TOKEN': accessToken },
                    });
                    if (mrRes.ok) {
                        detailed.merge_request = await mrRes.json();
                    }
                }
                else if (notif.subject_type === 'Issue') {
                    const issueUrl = `${baseUrl}/api/v4/projects/${notif.project_id}/issues/${notif.subject_id}`;
                    const issueRes = await fetch(issueUrl, {
                        headers: { 'PRIVATE-TOKEN': accessToken },
                    });
                    if (issueRes.ok) {
                        detailed.issue = await issueRes.json();
                    }
                }
                else if (notif.subject_type === 'Commit') {
                    const commitUrl = `${baseUrl}/api/v4/projects/${notif.project_id}/repository/commits/${notif.subject_id}`;
                    const commitRes = await fetch(commitUrl, {
                        headers: { 'PRIVATE-TOKEN': accessToken },
                    });
                    if (commitRes.ok) {
                        detailed.commit = await commitRes.json();
                    }
                }
                else if (notif.subject_type === 'Pipeline') {
                    const pipelineUrl = `${baseUrl}/api/v4/projects/${notif.project_id}/pipelines/${notif.subject_id}`;
                    const pipelineRes = await fetch(pipelineUrl, {
                        headers: { 'PRIVATE-TOKEN': accessToken },
                    });
                    if (pipelineRes.ok) {
                        detailed.pipeline = await pipelineRes.json();
                    }
                }
                // Fetch project info
                const projectUrl = `${baseUrl}/api/v4/projects/${notif.project_id}`;
                const projectRes = await fetch(projectUrl, {
                    headers: { 'PRIVATE-TOKEN': accessToken },
                });
                if (projectRes.ok) {
                    detailed.project = await projectRes.json();
                }
            }
            catch (e) {
                // Continue with basic notification info
            }
            detailedNotifications.push(detailed);
        }
        return detailedNotifications;
    }
    catch (error) {
        console.error('Error fetching GitLab notifications:', error);
        return [];
    }
}
/**
 * Format a GitLab notification into a user-friendly message
 */
export function formatGitLabNotification(notif) {
    const projectName = notif.project?.path_with_namespace || `Project #${notif.project_id}`;
    if (notif.subject_type === 'MergeRequest' && notif.merge_request) {
        const mr = notif.merge_request;
        return `[${projectName}] MR !${mr.iid}: ${mr.title} (${mr.state}) - ${mr.source_branch} → ${mr.target_branch}`;
    }
    if (notif.subject_type === 'Issue' && notif.issue) {
        const issue = notif.issue;
        return `[${projectName}] Issue #${issue.iid}: ${issue.title} (${issue.state})`;
    }
    if (notif.subject_type === 'Commit' && notif.commit) {
        const commit = notif.commit;
        return `[${projectName}] Commit ${commit.short_id}: ${commit.title}`;
    }
    if (notif.subject_type === 'Pipeline' && notif.pipeline) {
        const pipeline = notif.pipeline;
        return `[${projectName}] Pipeline #${pipeline.id}: ${pipeline.status} (${pipeline.ref})`;
    }
    return `[${projectName}] ${notif.subject_type} #${notif.subject_id}: ${notif.action_name}`;
}
//# sourceMappingURL=gitlab-notifications.js.map